import React, { useEffect, useState } from 'react'
import { Button, Drawer, Table, Tooltip, Typography } from 'antd'
import styled from 'styled-components'
import { useGetTaskDetail, useGetTaskTemplateList } from './utils'
import { SignTitle } from '@/components'
import { formatDateTime } from '@/utils/date'
import { TablePaginationConfig } from 'antd/es/table'
import { SorterResult } from 'antd/lib/table/interface'
import UsCaseModal from './usCase-modal'
import { t } from '@/i18n'

const { Text } = Typography

interface PropsType {
  open: boolean
  onClose: () => void
  id: string
}

interface TemplateItemProps {
  templateId: string
  name: string
  resourceAmount: number
  updateTime: string
}

export interface DetailPropsType {
  //  客户端
  agent?: string
  //  安全域
  areaName?: string
  createTime?: string
  //  任务周期天数 每月/每周
  cycleDays?: string
  //  任务id
  taskId?: string
  taskKind?: string
  //  任务名称
  taskName?: string
  //  任务执行时间
  taskTime?: string
  // 任务分组
  groupName?: string
  pluginNames?: string[]
  verificationType?: string
  verificationMode?: {
    agentToHost?: {
      agentId?: string
      agentName?: string
      hostName?: string
      hostIp?: string
      agentAlias?: string
      hostAlias?: string
      areaName?: string
    }
    agentToListener?: {
      agentId?: string
      agentName?: string
      hostName?: string
      nat?: string
      dport?: string
      dnatIp?: string
      agentAlias?: string
      hostAlias?: string
      areaName?: string
      hostIp?: string
    }
  }
  sampleDeliveryRate?: number
  priority?: number
  reporter?: {
    name: string
    email: string
  }[]
  reportFilter?: string[]
  devices?: {
    isOnline: boolean
    deviceType: string
    deviceName: string
    deviceIp: string
  }[]
}

const KindMap = {
  TaskKindDetailImmediately: t('立即'),
  TaskKindDetailBooking: t('预定'),
  TaskKindDetailCycleDay: t('每日'),
  TaskKindDetailCycleWeek: t('每周'),
  TaskKindDetailCycleMonth: t('每月'),
}

const WeekMap = {
  1: t('周一'),
  2: t('周二'),
  3: t('周三'),
  4: t('周四'),
  5: t('周五'),
  6: t('周六'),
  0: t('周日'),
}

const DetailDrawer: React.FC<PropsType> = ({ open, onClose, id }) => {
  const [getTaskDetail] = useGetTaskDetail()
  // 获取主任务关联模板列表
  const [getTaskTemplateList, getTaskTemplateListLoading] = useGetTaskTemplateList()
  const [taskDetail, setTaskDetail] = useState<DetailPropsType>()
  const [taskTemplateList, setTaskTemplateList] = useState<TemplateItemProps[]>()
  const [total, setTotal] = useState(0)
  const [searchParams, setSearchParams] = useState({
    page: 1,
    pageSize: 10,
    sortOrder: 'SORT_ORDER_UNSPECIFIED',
  })
  const [usCaseModal, setUsCaseModal] = useState<{
    open: boolean
    templateId: string
    taskId: string
  }>({
    open: false,
    templateId: '',
    taskId: '',
  })

  const columns = [
    {
      title: t('模板名称'),
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (text: string) => (
        <Text ellipsis={{ tooltip: true }} style={{ maxWidth: 250 }}>
          {text || '-'}
        </Text>
      ),
    },
    {
      title: t('用例数量'),
      dataIndex: 'resourceAmount',
      key: 'resourceAmount',
      width: 120,
      render: (text: string, record: TemplateItemProps) => (
        <Button
          type="link"
          onClick={() =>
            setUsCaseModal({
              open: true,
              templateId: record.templateId,
              taskId: id,
            })
          }
        >
          {text || '-'}
        </Button>
      ),
    },
    {
      title: t('更新时间'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 100,
      sorter: true,
      sortDirections: ['descend' as const, 'ascend' as const],
      render: text => (
        <Tooltip title={formatDateTime(text, 'standard')}>{formatDateTime(text, 'mdhm')}</Tooltip>
      ),
    },
  ]

  const buildDetailData = data => {
    const detailObj = JSON.parse(JSON.stringify(data))

    detailObj.taskKind = KindMap[detailObj.taskKind]
    detailObj.cycleDays =
      detailObj.taskKind === '每周'
        ? detailObj.cycleDays
            .replace(/{/g, '')
            .replace(/}/g, '')
            .split(',')
            .map(item => WeekMap[item])
        : detailObj.cycleDays.replace(/{/g, '').replace(/}/g, '').split(',')

    return detailObj
  }

  const fetchTaskTemplateList = async () => {
    try {
      const res = await getTaskTemplateList(id, searchParams)

      if (!res || !res.data) return

      setTaskTemplateList(res.data.items)
      setTotal(res.data.total)
    } catch (error) {}
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _,
    sorter: SorterResult<TemplateItemProps> | SorterResult<TemplateItemProps>[],
  ) => {
    let order = 'SORT_ORDER_UNSPECIFIED'

    if (!Array.isArray(sorter)) {
      order =
        sorter.order === 'descend'
          ? 'SORT_ORDER_DESCENDING'
          : sorter.order === 'ascend'
          ? 'SORT_ORDER_ASCENDING'
          : 'SORT_ORDER_UNSPECIFIED'
    }

    setSearchParams({
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      sortOrder: order,
    })
  }

  useEffect(() => {
    if (open) {
      fetchTaskTemplateList()
    }
  }, [searchParams, open])

  useEffect(() => {
    if (!open) return
    getTaskDetail(id)
      .then(res => {
        setTaskDetail(buildDetailData(res.data))
      })
      .catch(_ => {})

    return () => {
      setSearchParams({
        page: 1,
        pageSize: 10,
        sortOrder: 'SORT_ORDER_UNSPECIFIED',
      })
    }
  }, [open])

  return (
    <DrawerWrapper
      zIndex={1000}
      title={<SignTitle show>{t('任务详情')}</SignTitle>}
      width={600}
      open={open}
      onClose={onClose}
      footer={null}
    >
      <div className="task-config">
        <div className="title">
          <span>{t('任务配置信息')}</span>
        </div>
        <div className="content">
          <div className="item">
            <span className="label">{t('任务名称：')}</span>
            <span className="value">{taskDetail?.taskName}</span>
          </div>
          <div className="item">
            <span className="label">{t('任务分组：')}</span>
            <span className="value">{taskDetail?.groupName}</span>
          </div>
          <div className="item">
            <span className="label">{t('任务ID：')}</span>
            <span className="value">{taskDetail?.taskId}</span>
          </div>
          {taskDetail?.verificationMode?.agentToHost?.agentId !== '0' && (
            <div className="item vertical-item">
              <div className="label">{t('验证模式1')}</div>
              <div className="value">
                <div className="item">
                  <span>{t('模式名称：')}</span>
                  <span>{t('Agent → 主机')}</span>
                </div>
                <div className="item">
                  <span>Agent：</span>
                  <span>
                    {taskDetail?.verificationMode?.agentToHost?.agentAlias || t('未命名')}
                  </span>
                </div>
                <div className="item">
                  <span>{t('安全域：')}</span>
                  <span>
                    {taskDetail?.verificationMode?.agentToHost?.areaName || t('默认安全域')}
                  </span>
                </div>
              </div>
            </div>
          )}
          {taskDetail?.verificationMode?.agentToListener?.agentId !== '0' && (
            <div className="item vertical-item">
              <div className="label">{t('验证模式2')}</div>
              <div className="value">
                <div className="item">
                  <span>{t('模式名称：')}</span>
                  <span>Agent → Listener</span>
                </div>
                <div className="item">
                  <span>Agent：</span>
                  <span>
                    {taskDetail?.verificationMode?.agentToListener?.agentAlias || t('未命名')}
                  </span>
                </div>
                <div className="item">
                  <span>{t('安全域：')}</span>
                  <span>
                    {taskDetail?.verificationMode?.agentToListener?.areaName || t('默认安全域')}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="item special">
            <div className="label">{t('执行类型：')}</div>
            <div className="value">
              {taskDetail?.taskKind}
              &emsp;
              {taskDetail?.taskKind === '每周' && (
                <div className="day-wrapper">
                  {taskDetail?.cycleDays?.map(day => (
                    <div key={day} className="day">
                      <span>{day}</span>
                      &emsp;
                      <span>{taskDetail?.taskTime}</span>
                    </div>
                  ))}
                </div>
              )}
              {taskDetail?.taskKind === '每月' && (
                <div className="month-wrapper">
                  {taskDetail?.cycleDays?.map(day => (
                    <div key={day} className="day">
                      <span>
                        {day}
                        {t('号')}
                      </span>
                      &emsp;
                      <span>{taskDetail?.taskTime}</span>
                    </div>
                  ))}
                </div>
              )}
              &emsp;
              {taskDetail?.taskKind &&
                !['每周', '每月'].includes(taskDetail?.taskKind) &&
                taskDetail?.taskTime}
            </div>
          </div>
          <div className="item">
            <div className="label">{t('执行速率：')}</div>
            <div className="value">{taskDetail?.sampleDeliveryRate || '-'}</div>
          </div>
          <div className="item">
            <div className="label">{t('任务优先级：')}</div>
            <div className="value">{taskDetail?.priority || '-'}</div>
          </div>
          <div className="item vertical-item">
            <div className="label">{t('报表发送人：')}</div>
            <div className="value">
              {(taskDetail?.reporter &&
                taskDetail?.reporter.map(item => (
                  <div key={item.name} className="item">
                    {item.name}
                    &emsp;
                    <span>({item.email})</span>
                  </div>
                ))) ||
                '-'}
            </div>
          </div>
          <div className="item">
            <div className="label">{t('报表发送过滤条件：')}</div>
            <div className="value">
              {taskDetail?.reportFilter
                ?.map(item =>
                  item === 'conditionAllFailed' ? t('全部用例防护失效') : t('任意用例防护失效'),
                )
                .join('、') || '-'}
            </div>
          </div>
          <div className="item">
            <div className="label">{t('创建时间：')}</div>
            <div className="value">{taskDetail?.createTime}</div>
          </div>
        </div>
        {taskDetail?.devices && taskDetail?.devices?.length > 0 && (
          <>
            <div className="title">{t('关联安全设备')}</div>
            {taskDetail?.devices?.map(item => (
              <div className="device" key={item.deviceName}>
                <div className="device-type">
                  <span className="label">{t('设备类型：')}</span>
                  <span className="value">{item.deviceType}</span>
                </div>
                <div className="device-name">
                  <span className="label">{t('设备名称：')}</span>
                  <span className="value">{item.deviceName}</span>
                </div>
                <div className="device-ip">
                  <span className="label">{t('设备IP：')}</span>
                  <span className="value">{item.deviceIp}</span>
                </div>
                <div className="device-status">
                  <span
                    style={{
                      verticalAlign: 'middle',
                      display: 'inline-block',
                      marginRight: 4,
                      width: 6,
                      height: 6,
                      background: item.isOnline ? '#67C23A' : '#677280',
                      borderRadius: '50%',
                    }}
                  />

                  <span className="value">{item.isOnline ? t('在线') : t('离线')}</span>
                </div>
              </div>
            ))}
          </>
        )}
        <div className="title">{t('模板信息')}</div>
        <Table
          loading={getTaskTemplateListLoading}
          dataSource={taskTemplateList}
          columns={columns}
          bordered={false}
          rowKey="templateId"
          onChange={handleTableChange}
          pagination={{
            total,
            showTotal: totalNum => (
              <span>
                {t('共')}
                <span style={{ color: '#4238c9' }}>{totalNum}</span>
                {t('条')}
              </span>
            ),

            pageSize: searchParams.pageSize,
            current: searchParams.page,
            pageSizeOptions: ['10', '20', '30', '40', '50'],
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </div>
      <UsCaseModal
        open={usCaseModal.open}
        templateId={usCaseModal.templateId}
        taskId={usCaseModal.taskId}
        onClose={() => setUsCaseModal({ open: false, templateId: '', taskId: '' })}
      />
    </DrawerWrapper>
  )
}

export default DetailDrawer

const DrawerWrapper = styled(Drawer)`
  .ant-drawer-close {
    width: 36px;
    height: 36px;
    background: #d8d6f4;
    box-shadow: -1px 4px 10px 0px #c7c7c7;
    border-radius: 6px;
    border: none;
    margin-right: 0px;
    color: #4238c9;
    font-size: 16px;
    font-weight: 500;
    position: absolute;
    right: 0px;
    top: 0px;
    transition: transform 0.3s ease;

    &:hover {
      transform: translate(-10px, 8px);
      box-shadow: 0 0 0 0;
    }

    .ant-drawer-close-x {
      width: 100%;
      height: 100%;
      line-height: 36px;
      text-align: center;
    }
  }

  .ant-drawer-header {
    height: 56px;
    background: #f2f0ff;
    border-bottom: none;
    margin: 0;
  }
  .ant-drawer-body {
    padding: 20px 24px;
  }
  .task-config {
    display: flex;
    flex-direction: column;

    .title {
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 24px;
    }

    .content {
      display: flex;
      flex-direction: column;

      .item {
        margin-bottom: 20px;
        display: flex;

        &.special {
          min-height: 16px;
          max-height: 160px;
          overflow-y: scroll;
        }

        .label {
          min-width: 70px;
          color: #86909c;
          margin-right: 4px;
        }

        .value {
          height: 100%;
          color: #333333;
          display: flex;

          .day-wrapper,
          .month-wrapper {
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;
            width: 120px;

            .day {
              display: flex;

              span {
                margin-bottom: 8px;

                &:last-child {
                  margin-bottom: 0;
                }
              }
            }
          }
        }
      }

      .vertical-item {
        display: flex;
        flex-direction: column;

        .label {
          margin-bottom: 10px;
        }

        .value {
          background: #f8f9fb;
          border-radius: 6px;
          padding: 12px;
          display: flex;
          flex-direction: column;

          .item {
            display: flex;
            margin-bottom: 14px;

            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }
    }

    .device {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
      background: #f7f8fa;
      border-radius: 6px;
      padding: 10px 16px;
      line-height: 16px;

      .label {
        color: #86909c;
      }
    }
  }

  .task-template {
    margin-top: 4px;
    display: flex;
    flex-direction: column;

    .title {
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 16px;
    }

    .content {
      width: 432px;
      background: #f7f8fa;
      border-radius: 8px;
      padding: 12px;

      .item {
        margin-bottom: 16px;
        color: #666;
      }
    }
  }

  .ant-table-column-sorter {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .ant-table-column-sorters {
    justify-content: flex-start;

    .ant-table-column-title {
      flex: none;
    }
  }

  .ant-table-filter-column {
    justify-content: flex-start;

    .ant-table-column-title {
      flex: unset;
    }

    .ant-table-filter-trigger:hover {
      background: transparent;
    }
  }
`
